// OperationReport46Component.jsx
import React, { useEffect, useState, useMemo } from "react";
import { Container, Row, Col, Label, Table } from "reactstrap";
import Select from "react-select";
import Swal from "sweetalert2";
import DatePicker, { registerLocale } from "react-datepicker";
import { format, parseISO } from "date-fns";
import th from "date-fns/locale/th";
import { th as thBuddhist } from "date-fns/esm/locale";
import { addYears } from "date-fns/esm/fp";
import getYear from "date-fns/getYear";
import getMonth from "date-fns/getMonth";
import { GetVMdDept } from "../../share/api/MasterDataApi";

import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";

/* ===== locales ===== */
registerLocale("th-buddhist", {
  ...th,
  options: { ...th.options, weekStartsOn: 1 },
  formatLong: thBuddhist.formatLong,
});

const TH_MONTHS = [
  "มกราคม", "กุมภาพันธ์", "มีนาคม", "เมษายน", "พฤษภาคม", "มิถุนายน",
  "กรกฎาคม", "สิงหาคม", "กันยายน", "ตุลาคม", "พฤศจิกายน", "ธันวาคม"
];
const range = (start, end) => new Array(end - start).fill().map((_, i) => i + start);
const YEARS = range(1963, new Date().getFullYear() + 8);

/* ===== helpers ===== */
const N = (v) => Number(String(v ?? 0).replace(/,/g, "")) || 0;
const fmtMoney = (n) =>
  Number(String(n ?? 0).replace(/,/g, "")).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
const toThaiDateLabel = (v) => {
  if (!v) return "-";
  if (typeof v === "string" && /\s[ก-๙]/.test(v)) return v; // already BE label
  const d = typeof v === "string" ? parseISO(v) : v;
  if (!(d instanceof Date) || isNaN(d)) return "-";
  const be = addYears(543, d);
  const monthName = format(be, "MMMM", { locale: th });
  return `${format(be, "d")} ${monthName} ${format(be, "yyyy")}`;
};
const formatMonthYearBE = (date) => {
  if (!date) return "";
  const be = addYears(543, date);
  return format(be, "MMMM yyyy", { locale: th });
};
const groupBy = (arr, key) =>
  (arr || []).reduce((a, it) => {
    const k = String(it?.[key] ?? "");
    (a[k] ??= []).push(it);
    return a;
  }, {});
const isUniform = (rows, field) =>
  rows.every((r) => (r[field] ?? "") === (rows[0]?.[field] ?? ""));
const pick = (obj, ...keys) =>
  keys.map((k) => obj?.[k]).find((v) => v !== undefined && v !== null && String(v).trim() !== "") ?? "";

// แถวนี้เป็นของฝั่งไหน? (มีค่าในฟิลด์ชุด Test* -> TEST, ไม่งั้น ESTIMATE)
const isTestRow = (r) =>
  !!(r?.testLawOccupationName || r?.testOccupationName || r?.testReceiptDate);

/* ===== PDF Thai font (optional) ===== */
function base64FromArrayBuffer(buf) {
  const b = new Uint8Array(buf);
  const c = 0x8000;
  let s = "";
  for (let i = 0; i < b.length; i += c) {
    s += String.fromCharCode.apply(null, b.subarray(i, i + c));
  }
  return btoa(s);
}
let thaiFontLoaded = false;
async function ensureThaiFontLoaded() {
  if (thaiFontLoaded) return;
  const [regRes, boldRes] = await Promise.all([
    fetch("/fonts/Sarabun-Regular.ttf"),
    fetch("/fonts/Sarabun-Bold.ttf"),
  ]);
  const [regBuf, boldBuf] = await Promise.all([
    regRes.arrayBuffer(),
    boldRes.arrayBuffer(),
  ]);
  const regB64 = base64FromArrayBuffer(regBuf);
  const boldB64 = base64FromArrayBuffer(boldBuf);
  jsPDF.API.events.push([
    "addFonts",
    function () {
      this.addFileToVFS("Sarabun-Regular.ttf", regB64);
      this.addFileToVFS("Sarabun-Bold.ttf", boldB64);
      this.addFont("Sarabun-Regular.ttf", "Sarabun", "normal");
      this.addFont("Sarabun-Bold.ttf", "Sarabun", "bold");
    },
  ]);
  new jsPDF();
  thaiFontLoaded = true;
}

/* ===== Component ===== */
export function OperationReport46Component() {
  const [reportMonth, setReportMonth] = useState("");
  const [reportMonthDate, setReportMonthDate] = useState(null);
  const user = JSON.parse(localStorage.getItem("userData") || "{}");
  const roleId = String(user?.roleId ?? user?.roldId ?? "");
  const userDeptId = String(user?.deptId ?? "all");

  const [deptsData, setDeptsData] = useState([]);
  const [deptOptions, setDeptOptions] = useState([]);
  const [deptIdFilter, setDeptIdFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);

  // เงื่อนไขแสดงคอลัมน์หน่วยงาน
  const showDeptCol = (String(user?.roldId ?? roleId) === "4" && String(deptIdFilter || "all") === "all");

  // pagination states (กลุ่มต่อหน้า = จำนวน supportNo ต่อหน้า)
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20); // ปรับได้: 10,20,50,100

  useEffect(() => { GetVMdDept({ setDeptsData, setLoading }); }, []);
  useEffect(() => {
    const mapped = (deptsData || []).map((d) => ({
      value: String(d?.value ?? d?.deptId ?? d?.DeptId ?? d?.id ?? ""),
      label: String(d?.label ?? d?.deptName ?? d?.DeptName ?? d?.name ?? ""),
    }));
    if (roleId === "4") {
      setDeptOptions([{ value: "all", label: "ทุกหน่วยงาน" }, ...mapped]);
      setDeptIdFilter((p) => p ?? "all");
    } else if (roleId === "3") {
      const self = mapped.find((o) => o.value === userDeptId) || {
        value: userDeptId,
        label: "หน่วยงานของฉัน",
      };
      setDeptOptions([self]);
      setDeptIdFilter(userDeptId);
    } else {
      setDeptOptions(mapped);
      setDeptIdFilter((p) => p || (mapped.find((o) => o.value === userDeptId)?.value ?? ""));
    }
  }, [deptsData, roleId, userDeptId]);

  useEffect(() => {
    if (!reportMonth) {
      setReportMonthDate(null);
      return;
    }
    const [y, m] = reportMonth.split("-").map(Number);
    setReportMonthDate(new Date(y, m - 1, 1));
  }, [reportMonth]);

  // โหลดข้อมูลจาก API: listSupportOperation46
  const checkAndLoadMonth = async () => {
    try {
      setLoading(true);
      const fd = new FormData();
      fd.append("monthYear", reportMonth === "" ? "all" : reportMonth);
      fd.append("deptIdFilter", deptIdFilter || "all");

      const res = await fetch("/api/SupportOperation/listSupportOperation46", {
        method: "POST",
        headers: { token: user.token },
        body: fd,
      });
      const j = await res.json();
      const raw = Array.isArray(j?.dataenum) ? j.dataenum : [];

      // เติม DeptName แบบ normalize
      const mapped = raw.map(r => ({
        ...r,
        deptNameNormalized: pick(r, "deptName", "DeptName", "approveDeptName", "ApproveDeptName") || ""
      }));

      setItems(mapped);
      setPage(1); // รีเซ็ตไปหน้าแรกเมื่อโหลดข้อมูลใหม่
    } catch (e) {
      console.error(e);
      Swal.fire("โหลดข้อมูลไม่สำเร็จ", "", "error");
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  /* ===== totals (footer ทั้งหมด) ===== */
  const sum = (fn) => (items || []).reduce((s, r) => s + (fn(r) || 0), 0);
  const totalTestPerson = sum((r) => isTestRow(r) ? N(r.testPersonTotal) : 0);
  const totalTestAmount = sum((r) => isTestRow(r) ? N(r.testAmountTotal) : 0);
  const totalEstimatePerson = sum((r) => !isTestRow(r) ? N(r.estimatePersonTotal) : 0);
  const totalEstimateAmount = sum((r) => !isTestRow(r) ? N(r.estimateAmountTotal) : 0);
  const totalPayment = sum((r) => N(r.paymentAmount));

  /* ===== pagination derived ===== */
  const grouped = useMemo(() => Object.entries(groupBy(items, "supportNo")), [items]);
  const totalGroups = grouped.length;
  const pageCount = Math.max(1, Math.ceil(totalGroups / pageSize));
  const currentGroups = useMemo(() => {
    const start = (page - 1) * pageSize;
    return grouped.slice(start, start + pageSize);
  }, [grouped, page, pageSize]);

  /* ===== Excel build (ทุกข้อมูล, dynamic Dept col) ===== */
  const buildAoaAndMerges = () => {
    const row1 = [
      "ที่", "เลขที่คำขอ", "ชื่อผู้ขอรับเงินช่วยเหลือหรืออุดหนุน",
      ...(showDeptCol ? ["หน่วยงานที่อนุมัติคำขอ"] : []),
      "ศูนย์ทดสอบมาตรฐานฝีมือแรงงาน", "", "", "", "",
      "ศูนย์ประเมินความรู้ความสามารถ", "", "", "", "",
      "จำนวนเงินช่วยเหลือหรืออุดหนุน (บาท)", "วันที่จ่ายเงินช่วยเหลือหรืออุดหนุน"
    ];
    const row2 = [
      "", "", ...(showDeptCol ? [""] : []),
      "สาขาอาชีพ", "สาขา", "ว/ด/ป ที่นำส่งเงิน", "จำนวนผู้เข้ารับการทดสอบฯ (คน)", "จำนวนเงินค่าทดสอบที่ส่งเข้ากองทุน (บาท)",
      "สาขาอาชีพ", "สาขา", "ว/ด/ป ที่นำส่งเงิน", "จำนวนผู้เข้ารับการประเมินฯ (คน)", "จำนวนเงินค่าธรรมเนียมที่ส่งเข้ากองทุน (บาท)",
      "", ""
    ];
    const aoa = [row1, row2];

    const merges = [];
    // คอลัมน์ต้น (rowSpan 2)
    const fixed = showDeptCol ? 4 : 3; // No, SupportNo, SupportName, [Dept]
    for (let c = 0; c < fixed; c++) merges.push({ s: { r: 0, c }, e: { r: 1, c } });

    // กลุ่ม TEST 5 คอลัมน์
    merges.push({ s: { r: 0, c: fixed + 0 }, e: { r: 0, c: fixed + 4 } });
    // กลุ่ม ESTIMATE 5 คอลัมน์
    merges.push({ s: { r: 0, c: fixed + 5 }, e: { r: 0, c: fixed + 9 } });
    // เงิน/วันที่จ่าย rowSpan 2
    merges.push({ s: { r: 0, c: fixed + 10 }, e: { r: 1, c: fixed + 10 } });
    merges.push({ s: { r: 0, c: fixed + 11 }, e: { r: 1, c: fixed + 11 } });

    let cur = 2;
    Object.entries(groupBy(items, "supportNo")).forEach(([sn, rows], gIdx) => {
      const span = rows.length;
      const uPay = isUniform(rows, "paymentAmount");
      const uPayDt = isUniform(rows, "paymentDate");

      rows.forEach((r, idx) => {
        const testSide = isTestRow(r);
        const testCols = [
          testSide ? (r.testLawOccupationName || "-") : "",
          testSide ? (r.testOccupationName || "-") : "",
          testSide ? (r.testReceiptDate || "-") : "",
          testSide ? N(r.testPersonTotal) : "",
          testSide ? N(r.testAmountTotal) : "",
        ];
        const estCols = [
          !testSide ? (r.estimateLawOccupationName || "-") : "",
          !testSide ? (r.estimateOccupationName || "-") : "",
          !testSide ? (r.estimateReceiptDate || "-") : "",
          !testSide ? N(r.estimatePersonTotal) : "",
          !testSide ? N(r.estimateAmountTotal) : "",
        ];

        const row = [];
        row[0] = idx ? "" : gIdx + 1;
        row[1] = idx ? "" : (r.supportNo || "-");
        row[2] = idx ? "" : (r.supportName || "-");
        if (showDeptCol) row[3] = idx ? "" : (r.deptNameNormalized || "-");

        const base = fixed;
        row[base + 0] = testCols[0];
        row[base + 1] = testCols[1];
        row[base + 2] = testCols[2];
        row[base + 3] = testCols[3];
        row[base + 4] = testCols[4];
        row[base + 5] = estCols[0];
        row[base + 6] = estCols[1];
        row[base + 7] = estCols[2];
        row[base + 8] = estCols[3];
        row[base + 9] = estCols[4];
        row[base + 10] = uPay ? (idx ? "" : N(r.paymentAmount)) : N(r.paymentAmount);
        row[base + 11] = uPayDt ? (idx ? "" : r.paymentDate) : r.paymentDate;

        aoa.push(row);
        cur++;
      });

      // merge แนวตั้งคอลัมน์ SupportNo/SupportName/(Dept)
      merges.push({ s: { r: cur - span, c: 1 }, e: { r: cur - 1, c: 1 } });
      merges.push({ s: { r: cur - span, c: 2 }, e: { r: cur - 1, c: 2 } });
      if (showDeptCol) merges.push({ s: { r: cur - span, c: 3 }, e: { r: cur - 1, c: 3 } });
      // merge เงิน/วันที่จ่ายเมื่อ uniform
      if (isUniform(rows, "paymentAmount")) merges.push({ s: { r: cur - span, c: fixed + 10 }, e: { r: cur - 1, c: fixed + 10 } });
      if (isUniform(rows, "paymentDate")) merges.push({ s: { r: cur - span, c: fixed + 11 }, e: { r: cur - 1, c: fixed + 11 } });
    });

    return { aoa, merges, fixed };
  };

  const handleExportExcel = () => {
    if (!items.length) {
      Swal.fire("ไม่มีข้อมูลสำหรับส่งออก", "", "warning");
      return;
    }
    const { aoa, merges, fixed } = buildAoaAndMerges();
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws["!merges"] = merges;

    // ปรับความกว้างเมื่อมี Dept
    ws["!cols"] = (showDeptCol
      ? [{ wch: 4 }, { wch: 18 }, { wch: 34 }, { wch: 28 }]
      : [{ wch: 4 }, { wch: 18 }, { wch: 34 }]
    ).concat([
      { wch: 22 }, { wch: 18 }, { wch: 18 }, { wch: 20 }, { wch: 26 }, // TEST
      { wch: 22 }, { wch: 18 }, { wch: 18 }, { wch: 22 }, { wch: 28 }, // ESTIMATE
      { wch: 18 }, { wch: 22 }, // money/date
    ]);

    // แทรกแถวรวมท้ายไฟล์
    const r = aoa.length;
    ws[XLSX.utils.encode_cell({ r, c: 0 })] = { t: "s", v: "รวม" };
    // fixed = จำนวนคอลัมน์คงที่แรก (3 หรือ 4)
    const base = fixed;
    ws[XLSX.utils.encode_cell({ r, c: base + 3 })] = { t: "n", v: totalTestPerson };     // TEST person
    ws[XLSX.utils.encode_cell({ r, c: base + 4 })] = { t: "n", v: totalTestAmount };     // TEST money
    ws[XLSX.utils.encode_cell({ r, c: base + 8 })] = { t: "n", v: totalEstimatePerson }; // EST person
    ws[XLSX.utils.encode_cell({ r, c: base + 9 })] = { t: "n", v: totalEstimateAmount }; // EST money
    ws[XLSX.utils.encode_cell({ r, c: base + 10 })] = { t: "n", v: totalPayment };        // money
    // รวมช่องว่างด้านหน้าถึงก่อน TEST:base+2
    ws["!merges"].push({ s: { r, c: 0 }, e: { r, c: base + 2 } });

    XLSX.utils.book_append_sheet(wb, ws, "รายงาน ชอ.4-6");
    XLSX.writeFile(wb, `รายงาน_ชอ4-6_${reportMonth || "ทั้งหมด"}_${deptIdFilter}.xlsx`);
  };

  /* ===== PDF export (ทุกข้อมูล, dynamic Dept col) ===== */
  const handleExportPDF = async () => {
    if (!items.length) {
      Swal.fire("ไม่มีข้อมูลสำหรับส่งออก", "", "warning");
      return;
    }
    await ensureThaiFontLoaded();

    const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a3" });
    doc.setFont("Sarabun", "bold"); doc.setFontSize(16);
    doc.text(
      `แบบรายงานผลการพิจารณาอนุมัติฯ (แบบ ชอ.4-6) — ${formatMonthYearBE(reportMonthDate) || "ทุกเดือน"}`,
      40, 40
    );

    const headTop = [
      { content: "ที่", rowSpan: 2 },
      { content: "เลขที่คำขอ", rowSpan: 2 },
      { content: "ชื่อผู้ขอรับเงินช่วยเหลือหรืออุดหนุน", rowSpan: 2 },
      ...(showDeptCol ? [{ content: "หน่วยงานที่อนุมัติคำขอ", rowSpan: 2 }] : []),
      { content: "ศูนย์ทดสอบมาตรฐานฝีมือแรงงาน", colSpan: 5 },
      { content: "ศูนย์ประเมินความรู้ความสามารถ", colSpan: 5 },
      { content: "จำนวนเงินช่วยเหลือหรืออุดหนุน (บาท)", rowSpan: 2 },
      { content: "วันที่จ่ายเงินช่วยเหลือหรืออุดหนุน", rowSpan: 2 },
    ];
    const headSub = [
      { content: "สาขาอาชีพ" },
      { content: "สาขา" },
      { content: "ว/ด/ป ที่นำส่งเงิน" },
      { content: "จำนวนผู้เข้ารับการทดสอบฯ (คน)" },
      { content: "จำนวนเงินค่าทดสอบที่ส่งเข้ากองทุน (บาท)" },
      { content: "สาขาอาชีพ" },
      { content: "สาขา" },
      { content: "ว/ด/ป ที่นำส่งเงิน" },
      { content: "จำนวนผู้เข้ารับการประเมินฯ (คน)" },
      { content: "จำนวนเงินค่าธรรมเนียมที่ส่งเข้ากองทุน (บาท)" },
    ];

    const head = [headTop, headSub];

    const body = [];
    Object.entries(groupBy(items, "supportNo")).forEach(([sn, rows], gIdx) => {
      const span = rows.length;
      const uPay = isUniform(rows, "paymentAmount");
      const uPayDt = isUniform(rows, "paymentDate");

      rows.forEach((r, idx) => {
        const testSide = isTestRow(r);
        const row = [];
        row.push(
          idx ? "" : gIdx + 1,
          idx ? "" : (r.supportNo || "-"),
          idx ? "" : (r.supportName || "-"),
        );
        if (showDeptCol) row.push(idx ? "" : (r.deptNameNormalized || "-"));

        // TEST
        row.push(
          testSide ? (r.testLawOccupationName || "-") : "",
          testSide ? (r.testOccupationName || "-") : "",
          testSide ? (r.testReceiptDate || "-") : "",
          testSide ? fmtMoney(N(r.testPersonTotal)) : "",
          testSide ? fmtMoney(N(r.testAmountTotal)) : "",
        );
        // ESTIMATE
        row.push(
          !testSide ? (r.estimateLawOccupationName || "-") : "",
          !testSide ? (r.estimateOccupationName || "-") : "",
          !testSide ? (r.estimateReceiptDate || "-") : "",
          !testSide ? fmtMoney(N(r.estimatePersonTotal)) : "",
          !testSide ? fmtMoney(N(r.estimateAmountTotal)) : "",
        );

        row.push(
          uPay ? (idx ? "" : fmtMoney(N(r.paymentAmount))) : fmtMoney(N(r.paymentAmount)),
          uPayDt ? (idx ? "" : toThaiDateLabel(r.paymentDate)) : toThaiDateLabel(r.paymentDate),
        );

        body.push(row);
      });
    });

    doc.setFont("Sarabun", "normal");
    doc.autoTable({
      head, body, startY: 60,
      styles: { font: "Sarabun", fontSize: 10, cellPadding: 4, halign: "center", overflow: "linebreak" },
      headStyles: { fillColor: [255, 236, 224], textColor: [0, 0, 0], font: "Sarabun", fontStyle: "bold" },
      columnStyles: {
        2: { halign: "left" },                       // SupportName
        ...(showDeptCol ? { 3: { halign: "left" } } : {}),
        // fields text-heavy left align:
        ...(showDeptCol ? { 4: { halign: "left" }, 5: { halign: "left" }, 9: { halign: "left" }, 10: { halign: "left" } }
          : { 3: { halign: "left" }, 4: { halign: "left" }, 8: { halign: "left" }, 9: { halign: "left" } }),
      },
      didDrawPage: (data) => {
        const n = doc.internal.getNumberOfPages();
        doc.setFont("Sarabun", "normal"); doc.setFontSize(9);
        doc.text(`หน้า ${data.pageNumber} / ${n}`, data.settings.margin.left, doc.internal.pageSize.getHeight() - 10);
      },
    });

    const y = (doc.lastAutoTable?.finalY || 60) + 24;
    doc.setFont("Sarabun", "bold"); doc.setFontSize(12);
    doc.text(
      `รวม (ศูนย์ทดสอบ) คน: ${fmtMoney(totalTestPerson)} | เงิน: ${fmtMoney(totalTestAmount)}   —   ` +
      `(ศูนย์ประเมิน) คน: ${fmtMoney(totalEstimatePerson)} | เงิน: ${fmtMoney(totalEstimateAmount)}   —   ` +
      `ช่วยเหลือรวม: ${fmtMoney(totalPayment)} บาท`,
      40, y
    );
    doc.save(`รายงาน_ชอ4-6_${reportMonth || "all"}_${deptIdFilter}.pdf`);
  };

  /* ===== render ===== */
  return (
    <Container className="py-3">
      <Row className="g-3 align-items-end">
        <Col md={6}>
          <Label>หน่วยงานกรมพัฒนาฝีมือแรงงาน</Label>
          <Select
            options={deptOptions}
            value={deptOptions.find((o) => o.value === (deptIdFilter || "all")) || null}
            onChange={(opt) => setDeptIdFilter(opt?.value ?? "all")}
            isDisabled={roleId === "3"}
            className="text-center input-color"
            styles={{ control: (b) => ({ ...b, borderRadius: "20px" }), singleValue: (b) => ({ ...b, color: "#0A66C2" }) }}
            placeholder="เลือกหน่วยงาน"
            noOptionsMessage={() => "ไม่พบหน่วยงาน"}
          />
        </Col>
        <Col md={6}>
          <Label>รอบรายงาน (เดือน/ปี)</Label>
          <DatePicker
            selected={reportMonthDate}
            onChange={(date) => {
              if (date instanceof Date && !isNaN(date)) {
                const y = date.getFullYear();
                const m = String(date.getMonth() + 1).padStart(2, "0");
                setReportMonth(`${y}-${m}`);
                setReportMonthDate(new Date(y, Number(m) - 1, 1));
              } else {
                setReportMonth("");
                setReportMonthDate(null);
              }
            }}
            dateFormat="MMMM yyyy"
            showMonthYearPicker
            showFullMonthYearPicker
            locale="th-buddhist"
            placeholderText="เลือกเดือน/ปี (พ.ศ.)"
            value={formatMonthYearBE(reportMonthDate)}
            renderCustomHeader={({ date, changeYear, changeMonth, decreaseMonth, increaseMonth, prevMonthButtonDisabled, nextMonthButtonDisabled }) => (
              <div className="d-flex justify-content-between align-items-center px-2 py-1" style={{ gap: 8 }}>
                <button type="button" className="btn btn-sm btn-light" onClick={decreaseMonth} disabled={prevMonthButtonDisabled}>‹</button>
                <div className="d-flex align-items-center" style={{ gap: 8 }}>
                  <select className="form-select form-select-sm" style={{ width: 120 }} value={TH_MONTHS[getMonth(date)]} onChange={({ target: { value } }) => changeMonth(TH_MONTHS.indexOf(value))}>
                    {TH_MONTHS.map((m) => <option key={m} value={m}>{m}</option>)}
                  </select>
                  <select className="form-select form-select-sm" style={{ width: 100 }} value={getYear(date)} onChange={({ target: { value } }) => changeYear(Number(value))}>
                    {YEARS.map((y) => <option key={y} value={y}>{y + 543}</option>)}
                  </select>
                </div>
                <button type="button" className="btn btn-sm btn-light" onClick={increaseMonth} disabled={nextMonthButtonDisabled}>›</button>
              </div>
            )}
            customInput={
              <input
                className="form-control rounded-pill text-center input-color"
                readOnly
                value={formatMonthYearBE(reportMonthDate)}
                placeholder="เลือกเดือน/ปี (พ.ศ.)"
              />
            }
          />
        </Col>

        <Col md="auto" className="pt-4 d-flex justify-content-end gap-2">
          <button className="btn btn-primary" onClick={checkAndLoadMonth}>ตรวจสอบ/โหลดรายงาน</button>
          <button className="btn btn-outline-success" onClick={handleExportExcel}>ส่งออก Excel</button>
          <button className="btn btn-outline-danger" onClick={handleExportPDF}>ส่งออก PDF</button>
        </Col>
      </Row>

      <div className="mt-3">
        <Table bordered responsive>
          <thead className="table-light">
            <tr>
              <th rowSpan={2} className="text-center align-middle">ที่</th>
              <th rowSpan={2} className="text-center align-middle">เลขที่คำขอ</th>
              <th rowSpan={2} className="text-center align-middle">ชื่อผู้ขอรับเงินช่วยเหลือ<br />หรืออุดหนุน</th>
              {showDeptCol && (
                <th rowSpan={2} className="text-center align-middle">หน่วยงานที่อนุมัติคำขอ</th>
              )}

              <th colSpan={5} className="text-center align-middle">ศูนย์ทดสอบมาตรฐานฝีมือแรงงาน</th>
              <th colSpan={5} className="text-center align-middle">ศูนย์ประเมินความรู้ความสามารถ</th>

              <th rowSpan={2} className="text-center align-middle">จำนวนเงิน<br />ช่วยเหลือหรืออุดหนุน<br />(บาท)</th>
              <th rowSpan={2} className="text-center align-middle">วันที่จ่ายเงิน<br />ช่วยเหลือหรืออุดหนุน</th>
            </tr>
            <tr>
              <th className="text-center">สาขาอาชีพ</th>
              <th className="text-center">สาขา</th>
              <th className="text-center">ว/ด/ป ที่นำส่งเงิน</th>
              <th className="text-center">จำนวนผู้เข้ารับ<br />การทดสอบฯ (คน)</th>
              <th className="text-center">จำนวนเงินค่าทดสอบ<br />ที่ส่งเข้ากองทุน (บาท)</th>

              <th className="text-center">สาขาอาชีพ</th>
              <th className="text-center">สาขา</th>
              <th className="text-center">ว/ด/ป ที่นำส่งเงิน</th>
              <th className="text-center">จำนวนผู้เข้ารับ<br />การประเมินฯ (คน)</th>
              <th className="text-center">จำนวนเงินค่าธรรมเนียม<br />ที่ส่งเข้ากองทุน (บาท)</th>
            </tr>
          </thead>

          <tbody>
            {(currentGroups.length === 0) && (
              <tr><td colSpan={showDeptCol ? 16 : 15} className="text-center">ยังไม่มีรายการ</td></tr>
            )}

            {currentGroups.map(([sn, rows], gIdx) => {
              const span = rows.length;
              const uPay = isUniform(rows, "paymentAmount");
              const uPayDt = isUniform(rows, "paymentDate");

              return rows.map((r, idx) => {
                const testSide = isTestRow(r);
                const globalIndex = (page - 1) * pageSize + gIdx + 1; // เลขลำดับตามหน้า
                return (
                  <tr key={`${sn}-${idx}`}>
                    <td className="text-center">{idx === 0 ? globalIndex : ""}</td>

                    {idx === 0 && <td className="text-center" rowSpan={span}>{r.supportNo || "-"}</td>}
                    {idx === 0 && <td className="text-start" rowSpan={span}>{r.supportName || "-"}</td>}
                    {showDeptCol && idx === 0 && <td className="text-start" rowSpan={span}>{r.deptNameNormalized || "-"}</td>}

                    {/* TEST side */}
                    <td className="text-start">{testSide ? (r.testLawOccupationName || "-") : ""}</td>
                    <td className="text-start">{testSide ? (r.testOccupationName || "-") : ""}</td>
                    <td className="text-center">{testSide ? (r.testReceiptDate || "-") : ""}</td>
                    <td className="text-end">{testSide ? fmtMoney(N(r.testPersonTotal)) : ""}</td>
                    <td className="text-end">{testSide ? fmtMoney(N(r.testAmountTotal)) : ""}</td>

                    {/* ESTIMATE side */}
                    <td className="text-start">{!testSide ? (r.estimateLawOccupationName || "-") : ""}</td>
                    <td className="text-start">{!testSide ? (r.estimateOccupationName || "-") : ""}</td>
                    <td className="text-center">{!testSide ? (r.estimateReceiptDate || "-") : ""}</td>
                    <td className="text-end">{!testSide ? fmtMoney(N(r.estimatePersonTotal)) : ""}</td>
                    <td className="text-end">{!testSide ? fmtMoney(N(r.estimateAmountTotal)) : ""}</td>

                    {/* เงินช่วยเหลือ (merge เมื่อซ้ำกันทั้งกลุ่ม) */}
                    {uPay
                      ? (idx === 0 ? <td className="text-end" rowSpan={span}>{fmtMoney(N(rows[0].paymentAmount))}</td> : null)
                      : <td className="text-end">{fmtMoney(N(r.paymentAmount))}</td>
                    }

                    {/* วันที่จ่าย (merge เมื่อซ้ำกันทั้งกลุ่ม) */}
                    {uPayDt
                      ? (idx === 0 ? <td className="text-center" rowSpan={span}>{toThaiDateLabel(rows[0].paymentDate)}</td> : null)
                      : <td className="text-center">{toThaiDateLabel(r.paymentDate)}</td>
                    }
                  </tr>
                );
              });
            })}
          </tbody>

          {items.length > 0 && (
            <tfoot>
              <tr>
                <td colSpan={showDeptCol ? 7 : 6} className="text-center fw-bold">รวมทั้งหมด</td>
                <td className="text-end fw-bold">{fmtMoney(totalTestPerson)}</td>
                <td className="text-end fw-bold">{fmtMoney(totalTestAmount)}</td>
                <td colSpan={3} />
                <td className="text-end fw-bold">{fmtMoney(totalEstimatePerson)}</td>
                <td className="text-end fw-bold">{fmtMoney(totalEstimateAmount)}</td>
                <td className="text-end fw-bold">{fmtMoney(totalPayment)}</td>
                <td />
              </tr>
            </tfoot>
          )}
        </Table>
      </div>

      {/* Pagination controls */}
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mt-2">
        <div className="d-flex align-items-center gap-2">
          <span>แสดงต่อหน้า:</span>
          <select
            className="form-select form-select-sm"
            style={{ width: 100 }}
            value={pageSize}
            onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
          >
            {[10, 20, 50, 100].map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <span className="ms-2">
            ทั้งหมด {totalGroups.toLocaleString()} รายการ
          </span>
        </div>

        <div className="d-flex align-items-center gap-2">
          <button className="btn btn-sm btn-outline-secondary" disabled={page <= 1} onClick={() => setPage(1)}>{'«'}</button>
          <button className="btn btn-sm btn-outline-secondary" disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}>{'‹'}</button>
          <span>หน้า {page} / {pageCount}</span>
          <button className="btn btn-sm btn-outline-secondary" disabled={page >= pageCount} onClick={() => setPage(p => Math.min(pageCount, p + 1))}>{'›'}</button>
          <button className="btn btn-sm btn-outline-secondary" disabled={page >= pageCount} onClick={() => setPage(pageCount)}>{'»'}</button>
        </div>
      </div>

      {loading && (
        <div className="position-fixed bottom-0 end-0 p-3">
          <div className="badge bg-secondary">กำลังโหลด...</div>
        </div>
      )}
    </Container>
  );
}
